/** Orbit band: 0 = inner (current frontier), 1–2 = mid/outer history, 3 = meta */

/** Power-map bubble color by relationship to the project (default founder). */
export const MAP_ROLE_ACCENTS = {
  founder: '#6ecf8c',
  investor: '#5eb0e8',
  acquired: '#e8933a',
  inactive: '#8a9a9e',
  fellowship: '#b388ff',
  board: '#d9578a',
};

export const MAP_LEGEND_ENTRIES = [
  { role: 'founder', label: 'Founder' },
  { role: 'investor', label: 'Investor' },
  { role: 'acquired', label: 'Acquired' },
  { role: 'fellowship', label: 'Fellowship' },
  { role: 'board', label: 'Board' },
  { role: 'inactive', label: 'Inactive' },
];

export function mapAccentForRole(role) {
  const r = role || 'founder';
  return MAP_ROLE_ACCENTS[r] ?? MAP_ROLE_ACCENTS.founder;
}

export const CENTER_NODE = {
  id: 'kai',
  title: 'Kai Micah Mills',
  subtitle: 'High school dropout, cosmist, Thiel Fellow',
  status: null,
  date: null,
  description: `Kai Micah Mills is a serial founder working on the death problem. A cosmist, he works toward eliminating death and universal resurrection for all life, a goal known as the Common Task. He left school at 14 and spent more than a decade building fast-moving tech startups, selling his first company at 16.

Inspired early by Dmitry Itskov and the 2045 Initiative, he now works across the life extension field. He founded Cryopets to save his cat and advance whole-organism cryopreservation, co-founded CryoDAO and HydraDAO to fund and incubate moonshot research in cryopreservation and replacement, and has founded other initiatives in the space.`,
  portrait: '/kai.png',
  events: [
    { kind: 'Born', date: '5 Jun 1998', place: 'Frederick, Maryland' },
    { kind: 'Residence', date: '2012', place: 'Saratoga Springs, Utah' },
    { kind: 'Residence', date: '2014', place: 'Riverton, Utah' },
    { kind: 'Residence', date: '2017', place: 'Bountiful, Utah' },
    { kind: 'Residence', date: '2018', place: 'Lehi, Utah' },
    { kind: 'Residence', date: '2019', place: 'Sandy, Utah' },
    { kind: 'Residence', date: '2020', place: 'Kirkland, Washington' },
    { kind: 'Residence', date: '2021', place: 'Salt Lake City, Utah' },
    { kind: 'Residence', date: '2025', place: 'San Antonio, Texas' },
    { kind: 'Residence', date: '2026', place: 'New Braunfels, Texas' },
  ],
  images: ['/kai-3.JPG'],
  icon: 'kai.png',
  links: [
    { label: 'X', href: 'https://x.com/kaimicahmills' },
    { label: 'LinkedIn', href: 'https://linkedin.com/in/kaimicahmills' },
    { label: 'YouTube', href: 'https://youtube.com/@kaimicahmills' },
    { label: 'GitHub', href: 'https://github.com/kaimicahmills' },
    { label: 'Instagram', href: 'https://instagram.com/cryokai' },
    { label: 'Email', href: 'mailto:kaimicahmills@gmail.com' },
    { separator: true, key: 'press' },
    { label: 'The Free Press', href: 'https://www.thefp.com/p/tech-gods-immortality-live-forever' },
    {
      label: 'Business Insider',
      href: 'https://www.businessinsider.com/thiel-fellow-kai-micah-mills-cryopets-cryonics-longevity-animals-pets-2023-4',
    },
  ],
};

export const HIDDEN_NODE = {
  id: 'hidden',
  title: 'Hidden Menu',
  subtitle: 'Nice find lol',
  status: null,
  date: null,
  description:
    "Congratulations on finding the hidden menu! I didn't imagine my life going this way... I grew up in a very religious mormon family. Unfortunately, I believe this is the end. This is all we have, and this is all there is. Death is unacceptable. We have to solve it. This is what I will be dedicating the rest of my life to.",
  images: ['/hidden/hidden-1.jpg', '/hidden/hidden-2.jpg', '/hidden/hidden-3.jpg'],
  icon: null,
  orbitBand: 3,
  bubbleRadius: 24,
  accent: '#b8a060',
};

/** Lines between main portfolio bubbles (e.g. shared orgs across the map). */
export const MAIN_NODE_LINKS = [
  { fromId: 'cryopets', toId: 'thiel_fellowship' },
  { fromId: 'cryopets', toId: 'foresight_fellowship' },
  { fromId: 'cryopets', toId: 'longevity_biotech_fellowship' },
  { fromId: 'hydradao', toId: 'abf' },
  { fromId: 'cryodao', toId: 'abf' },
  { fromId: 'cryodao', toId: 'hydradao' },
];

/** Smaller bodies that orbit a parent main node. */
export const MOON_NODES = [
  {
    id: 'abf',
    parentId: 'cryopets',
    label: 'American Biostasis Foundation',
    accent: '#7ecf9a',
    mapRole: 'founder',
    icon: 'abf.png',
    detailSubtitle: 'Biostasis Facility',
    detailStatus: 'Founder',
    detailDate: '2025 - Present',
    detailDescription: `ABF is a nonprofit building a flagship biostasis research lab and long-term patient care facility, pooling Cryopets, Tomorrow Bio, CryoDAO, and HydraDAO to take patients from multiple providers. Kai co-founded it with Emil Kendziorra in 2025.`,
    detailLinks: [{ label: 'americanbiostasis.org', href: 'https://www.americanbiostasis.org' }],
    detailImages: [
      '/abf/abf-render.jpg',
      '/abf/abf3.jpg',
      '/abf/abffull.png',
      '/abf/GqDjLRjXgAAtC7n.jpg',
      '/abf/GrCd14oWUAAkdIh.jpg',
      '/abf/GsKcXJlX0AANn-l.jpg',
      '/abf/Gw1DVCdWgAAGra0.jpg',
    ],
  },
  {
    id: 'moon_dowell',
    parentId: 'hydradao',
    label: 'Theseus',
    accent: '#7dcf9a',
    mapRole: 'founder',
    icon: 'theseus.svg',
    detailSubtitle: 'Tissue Repair',
    detailStatus: 'Founder',
    detailDate: '2025 - Present',
    detailDescription: `Theseus is the company spun out of Dowell Bio, the HydraDAO-funded spinal cord research project. Kai is a co-founder, with Michael Lebenstein-Gumovski, Andrei Panferov, and Eli Mohamad.

Theseus is developing an AI-guided approach to tissue repair, starting with therapies to reconnect an injured spinal cord. The first program treats neural fusion as one therapy: preparing the repair site, an investigational PEG–chitosan fusogen, a delivery protocol, and recovery tracking. Published complete-transection studies report restored movement in rats, rabbits, and swine.`,
    detailLinks: [
      { label: 'theseus.bio', href: 'https://theseus.bio' },
      {
        label: 'HydraDAO project page',
        href: 'https://hydradao.org/projects/dowell-spinal-fusogens-financing',
      },
    ],
    detailImages: [
      '/dowell/G1OKUIVXwAAJvkH.jpg',
      '/dowell/GufqqPWW0AEStIX.jpg',
      '/dowell/Gz1T9QLXwAEveE_.jpg',
    ],
    detailArticles: [
      {
        label: 'MIT Technology Review — Six wild ideas for perpetuating your mind',
        href: 'https://www.technologyreview.com/2026/10/15/1145819/six-wild-ideas-perpetuating-your-mind-cognitive-immortality',
      },
      {
        label: 'Lifeboat — Advancing frontiers of cryopreservation & biological replacement',
        href: 'https://lifeboat.com/blog/2025/01/eli-mohamad-kai-micah-mills-advancing-frontiers-of-cryopreservation-biological-replacement',
      },
      {
        label: 'Biostasis Standard — The Rise of DeSci',
        href: 'https://biostasis.substack.com/p/the-rise-of-desci',
      },
    ],
  },
  {
    id: 'moon_kindbio',
    parentId: 'hydradao',
    label: 'Kind Bio',
    accent: '#79d4a0',
    mapRole: 'investor',
    icon: 'kindbio.png',
    detailSubtitle: 'HydraDAO funded research',
    detailStatus: 'Investor',
    detailDate: '2026 - Present',
    detailDescription: `HydraDAO is investing $250,000 in Kind Bio, a company building an integrated organ network (ION), also known as a bodyoid, that uses artificial genomic constructs to grow interdependent peripheral organs.

Kind Bio's approach is designed for ethical, scalable xenotransplant organ production to remove organ shortage as a bottleneck and make replacement medicine more practical for patients with organ failure. HydraDAO's thesis is that bodyoids can become replacement bodies for whole-body replacement.`,
    detailLinks: [{ label: 'kindbiotechnology.com', href: 'https://kindbiotechnology.com' }],
    detailImages: ['/kindbio/img1.jpg', '/kindbio/bodyoid.png'],
    detailArticles: [
      {
        label: 'Core Memory — The man growing organs on demand',
        href: 'https://www.corememory.com/p/the-man-growing-organs-on-demand-kind-bio-justin-rebo',
      },
      {
        label: 'HydraDAO — Kind Bio integrated organ networks',
        href: 'https://hydradao.org/projects/kind-bio',
      },
    ],
  },
  {
    id: 'moon_sbc',
    parentId: 'cryodao',
    label: 'Space Biostasis Coalition',
    accent: '#8ddb9e',
    icon: 'sbc.png',
    mapRole: 'founder',
    detailSubtitle: 'Coalition',
    detailStatus: 'Founder',
    detailDate: '2026 - Present',
    detailDescription: `Launched out of CryoDAO, the Space Biostasis Coalition is a US and Swiss nonprofit that brings together researchers, industry, and policymakers on biostasis for long-duration spaceflight: reversible suspended animation to ease resource demand and crew stress.

The coalition focuses on drawing serious funding into frontier biostasis research and hosts the Space Biostasis Summit; the first event is planned for February 2027 at Space Center Houston.`,
    detailLinks: [{ label: 'spacebiostasis.org', href: 'https://spacebiostasis.org' }],
    detailImages: ['/sbc/sbc.png', '/sbc/sbc2.jpg'],
  },
  {
    id: 'moon_gaia',
    parentId: 'cryodao',
    label: 'Gaia Life',
    accent: '#6ec98a',
    icon: 'gaia.png',
    mapRole: 'investor',
    detailSubtitle: 'CryoDAO funded project',
    detailStatus: 'Investor',
    detailDate: '2024 - Present',
    detailDescription: `In 2024 CryoDAO put $350,000 into Gaia Life toward live births from vitrified whole ovaries in sheep, on top of NIH vitrification and nanowarming work. Strong results would matter for FDA paths on vitrified organs and fertility care.

The sheep pipeline is moving: dozens perfused and vitrified with micro-CT confirmation, orthotopic replants with reperfusion, a new groin transplant site for throughput, hormone recovery underway, and NCI aligned on a ~$9M continuation track with federal plus matched funding.`,
    detailLinks: [
      {
        label: 'CryoDAO project page',
        href: 'https://www.cryodao.org/project/live-births-from-vitrified-and-replanted-whole-ovaries',
      },
    ],
    detailImages: ['/gaia/G8jDMLmW4AAfsdw.jpg', '/gaia/GjTNMO9WoAAD1N5.jpg'],
  },
  {
    id: 'moon_oxford',
    parentId: 'cryodao',
    label: 'Oxford Cryotechnology',
    accent: '#8bc98f',
    icon: 'oxfordcryotech.png',
    mapRole: 'investor',
    detailSubtitle: 'CryoDAO funded project',
    detailStatus: 'Investor',
    detailDate: '2023 - Present',
    detailDescription: `CryoDAO funded $50k with Oxford Cryotechnology for cryoprotectant molecular profiling and ML: build expression data where it barely exists today, then model safer CPA combinations for primary cell lines.

Led by Dr. João Pedro de Magalhães and Dr. Roman Bauer. Running since 2023.`,
    detailLinks: [
      {
        label: 'CryoDAO project page',
        href: 'https://www.cryodao.org/project/molecular-profiling-and-computational-modelling-for-novel-cryoprotectants',
      },
    ],
    detailImages: ['/oxford/photo_London-2.jpg'],
  },
  {
    id: 'moon_cryorat',
    parentId: 'cryodao',
    label: 'CryoRat',
    accent: '#9dd9a8',
    icon: 'cryorat.png',
    mapRole: 'founder',
    detailSubtitle: 'CryoDAO program',
    detailStatus: 'Founder',
    detailDate: '2024 - Present',
    detailDescription: `CRYORAT is CryoDAO's whole-body rat program with Advanced Neural Biosciences and Aschwin de Wolf: high sub-zero cryoprotection toward eventual revival, $900k from 2024.

The plan is perfusion optimization, CPA mapping across organs and brain, viability readouts, in-house micro-CT for fast iteration, and staged revival attempts as milestones land.`,
    detailLinks: [
      {
        label: 'CryoDAO project page',
        href: 'https://www.cryodao.org/project/high-sub-zero-cryopreservation-and-revival-of-a-small-mammal',
      },
    ],
    detailImages: [
      '/cryorat/G6NRr_EWEAAyZEg.jpg',
      '/cryorat/GebTm4RaYAA3utT.jpg',
      '/cryorat/GfMVVkAagAA4yz3.jpg',
      '/cryorat/GmrSrhMaoAA353L.jpg',
      '/cryorat/GrB1uASXQAAKZI2.jpg',
      '/cryorat/GspUgkJW8AA-Nsk.jpg',
      '/cryorat/GtxWG4YW4AAErF2.jpg',
      '/cryorat/GwTO42iWgAAJBFZ.jpg',
    ],
    detailArticles: [
      {
        label: 'Biostasis Standard — The Rise of DeSci (CryoRat)',
        href: 'https://biostasis.substack.com/p/the-rise-of-desci',
      },
      {
        label: 'Biostasis Standard — CryoDAO (CryoRat project)',
        href: 'https://biostasis.substack.com/p/cryodao',
      },
      {
        label: 'Levity — “It\'s going to be Frankenstein for a while”',
        href: 'https://reachlevity.com/p/it-s-going-to-be-frankenstein-for-a-while',
      },
    ],
  },
];

export const ORBIT_NODES = [
  {
    id: 'inevitable',
    title: 'Inevitable',
    subtitle: 'Decentralized Science Launchpad',
    status: 'Founder',
    mapRole: 'founder',
    date: '2025 - Present',
    description: `Inevitable bundles years of DeSci work across CryoDAO, CryoRat, HydraDAO, ErectusDAO, and Stasis into one incubator and venture studio for frontier life extension.`,
    images: ['/inevitable/inevbanner.png', '/inevitable/inevspeak.jpg', '/inevitable/inevban.png'],
    icon: 'inevitable.png',
    orbitBand: 0,
    bubbleRadius: 34,
    accent: '#5ee7a8',
    links: [{ label: 'inevitable.science', href: 'https://inevitable.science' }],
    articles: [
      {
        label: 'Levity — “It\'s going to be Frankenstein for a while”',
        href: 'https://reachlevity.com/p/it-s-going-to-be-frankenstein-for-a-while',
      },
      {
        label: 'Biostasis Standard — The Rise of DeSci',
        href: 'https://biostasis.substack.com/p/the-rise-of-desci',
      },
    ],
  },
  {
    id: 'hydradao',
    title: 'HydraDAO',
    subtitle: 'Replacement Research',
    status: 'Founder',
    mapRole: 'founder',
    date: '2024 - Present',
    description: `HydraDAO is a bet on whole-body replacement: cloned or synthetic embryos, neocortex knockouts for head or brain transplant, then progressive brain tissue swap. The treasury also backs a wider slice of replacement work, including organ replacement, bodyoids, and other engineering-heavy bets on that path. So far, HydraDAO has raised over $4.1M to push these experiments forward. Same funding spirit as CryoDAO, more engineering than waiting on aging biology.`,
    images: [
      '/hydradao/hydradao-1.jpg',
      '/hydradao/hydradao-2.jpg',
      '/hydradao/hydradao-3.png',
      '/hydradao/GeeglJ5aEAMnaVr.jpg',
      '/hydradao/GenLJx6agAAeBhx.jpg',
      '/hydradao/GhFeBBsW0AA8n17.jpg',
      '/hydradao/GlDdXKCW8AAxmbR.jpg',
      '/hydradao/GqN4HSBXsAIyyp5.jpg',
      '/hydradao/GwMeYt7WQAERWkm.jpg',
      '/hydradao/Gz1T9QLXwAEveE_.jpg',
    ],
    icon: 'hydradao.png',
    orbitBand: 1,
    bubbleRadius: 30,
    accent: '#6edc95',
    links: [{ label: 'hydradao.org', href: 'https://hydradao.org' }],
    articles: [
      {
        label: 'Lifeboat — Advancing frontiers of cryopreservation & biological replacement',
        href: 'https://lifeboat.com/blog/2025/01/eli-mohamad-kai-micah-mills-advancing-frontiers-of-cryopreservation-biological-replacement',
      },
      {
        label: 'Levity — “It\'s going to be Frankenstein for a while”',
        href: 'https://reachlevity.com/p/it-s-going-to-be-frankenstein-for-a-while',
      },
      {
        label: 'Biostasis Standard — The Rise of DeSci',
        href: 'https://biostasis.substack.com/p/the-rise-of-desci',
      },
      {
        label: 'Biostasis Standard — CryoDAO (covers HydraDAO)',
        href: 'https://biostasis.substack.com/p/cryodao',
      },
    ],
  },
  {
    id: 'cryodao',
    title: 'CryoDAO',
    subtitle: 'Cryopreservation Research',
    status: 'Founder',
    mapRole: 'founder',
    date: '2023 - Present',
    description: `Kai co-initiated CryoDAO with Emil Kendziorra, Vincent Weisser, and Eli Mohamad: a nonprofit that funds cryopreservation research. It has backed sheep ovary vitrification and transplantation, whole non-hibernating mammal high subzero preservation and revival, and a number of other projects in the field. To date, CryoDAO has raised over $5M for that work.`,
    images: [
      '/cryodao/cryodao-1.jpg',
      '/cryodao/cryodao-2.jpg',
      '/cryodao/cryodao-3.jpg',
      '/cryodao/F5lXaZFaUAAetXv.jpg',
      '/cryodao/GL6dKGhakAAk46r.jpg',
      '/cryodao/GMRn2Pda0AAOxVT.jpg',
      '/cryodao/GcEG7tzWsAAJq-E.jpg',
      '/cryodao/Gd4Ftu_bgAAza9N.jpg',
    ],
    icon: 'cryodao.png',
    orbitBand: 1,
    bubbleRadius: 32,
    accent: '#86f0a8',
    links: [{ label: 'cryodao.org', href: 'https://cryodao.org' }],
    articles: [
      {
        label: 'Biostasis Standard — CryoDAO with Kai Micah Mills',
        href: 'https://biostasis.substack.com/p/cryodao',
      },
      {
        label: 'Biostasis Standard — The Rise of DeSci',
        href: 'https://biostasis.substack.com/p/the-rise-of-desci',
      },
      {
        label: 'bio.xyz — First cohort of pioneering bioDAOs',
        href: 'https://www.bio.xyz/blog/celebrating-the-first-cohort-of-pioneering-biotech-daos',
      },
      {
        label: 'Juicecast — Funding cryopreservation research with crypto',
        href: 'https://pocketcasts.com/podcast/the-juicecast/4bc9c7d0-4058-013a-d680-0acc26574db2/ep-26-funding-cryopreservation-research-with-crypto-dr-emil-kendziorra-kai-micah-mills-from-cryodao/4f2ac0cd-89aa-404c-8367-c9cee9bd09e5',
      },
      {
        label: 'DeSci Podcast — Advancing cryopreservation research using Web3',
        href: 'https://share.snipd.com/episode/08ab7963-2a9c-41ef-accf-dbabe3c94419',
      },
      {
        label: 'Lifeboat — Advancing frontiers of cryopreservation & biological replacement',
        href: 'https://lifeboat.com/blog/2025/01/eli-mohamad-kai-micah-mills-advancing-frontiers-of-cryopreservation-biological-replacement',
      },
      {
        label: 'Levity — “It\'s going to be Frankenstein for a while”',
        href: 'https://reachlevity.com/p/it-s-going-to-be-frankenstein-for-a-while',
      },
    ],
  },
  {
    id: 'cryopets',
    title: 'Cryopets',
    subtitle: 'Cryopreservation Service Provider',
    status: 'Founder',
    mapRole: 'founder',
    date: '2022 - Present',
    description: `After a decade in tech, Kai started Cryopets to attack death as an engineering problem: whole-body cryopreservation for mammals, starting with pets. Cryopets has raised $1.5M to build the company, backed by the Thiel Foundation and others.`,
    images: [
      '/cryopets/cryopets-1.jpg',
      '/cryopets/cryopets-2.jpg',
      '/cryopets/cryopets-3.jpg',
      '/cryopets/G3-4iyEWYAAg2S0.jpg',
      '/cryopets/G4ClJMAWkAADtqc.jpg',
      '/cryopets/G5RERbSXgAAY7gl.jpg',
      '/cryopets/G8R0L5RWsAQ04UD.jpg',
      '/cryopets/G8R8p6OXUAwRF_l.jpg',
      '/cryopets/Gv0Ic-7WAAAntJT.jpg',
      '/cryopets/seo.png',
    ],
    icon: 'cryopets.png',
    orbitBand: 0,
    bubbleRadius: 30,
    accent: '#a5e8c8',
    links: [{ label: 'cryopets.com', href: 'https://cryopets.com' }],
    articles: [
      {
        label: 'Nature — Can science make your dog live longer?',
        href: 'https://www.nature.com/articles/d41586-026-03447-8',
      },
      {
        label: 'OpenVC — How Cryopets raised $1.5M',
        href: 'https://www.openvc.app/funding-stories/cryopets',
      },
      {
        label: 'Business Insider — Thiel Fellow cryopreserving pets',
        href: 'https://www.businessinsider.com/thiel-fellow-kai-micah-mills-cryopets-cryonics-longevity-animals-pets-2023-4',
      },
      {
        label: 'The Free Press — Tech messiahs who want to deliver us from death',
        href: 'https://www.thefp.com/p/tech-gods-immortality-live-forever',
      },
      {
        label: 'Daily Mail — Utah firm preserving pets until revival',
        href: 'https://www.dailymail.com/sciencetech/article-12091381/Company-backed-billionaire-Peter-Thiel-cryogenically-freezing-PETS-30-000.html',
      },
      {
        label: 'Benzinga — Pet owners and billionaires invest in cryonics',
        href: 'https://www.benzinga.com/news/23/05/32447273/pet-owners-and-billionaires-invest-in-cryonics-to-resurrect-beloved-animals',
      },
      {
        label: 'Levity — “It\'s going to be Frankenstein for a while”',
        href: 'https://reachlevity.com/p/it-s-going-to-be-frankenstein-for-a-while',
      },
      {
        label: 'Tremendous — Meet my latest investment: Cryopets',
        href: 'https://tremendous.blog/2026/02/05/meet-my-latest-investment-cryopets/',
      },
      {
        label: 'Foresight — Getting cryonics to the masses',
        href: 'https://foresight.org/resource/kai-micah-mills-ceo-of-cryopets-getting-cryonics-to-the-masses/',
      },
      {
        label: 'Narratives Podcast — Cryonics with Kai Micah Mills',
        href: 'https://narrativespodcast.com/2022/03/28/87-cryonics-with-kai-micah-mills/',
      },
      {
        label: 'Tomorrow Bio / VitaDAO — Science of biostasis and cryopreservation',
        href: 'https://www.tomorrow.bio/newsroom/the-science-of-biostasis-and-cryopreservation-with-kai-micah-mills-dr-emil-kendziorra',
      },
    ],
  },
  {
    id: 'branch',
    title: 'Branch',
    subtitle: 'Game Studio',
    status: 'Inactive',
    date: '2020 - 2021',
    mapRole: 'inactive',
    description: `Kai co-founded Branch with Dayton Mills in 2020 from his gaming roots: a 2D office app where remote teams could connect, talk spatially, knock on doors, pass each other in hallways, and have fun while working. It was his first startup that really broke into Silicon Valley.

Branch raised a $1.6M pre-seed from Naval Ravikant, Sahil Lavingia, Homebrew, and others, and built a larger team including Connor Hollasch and Ben Adamsky. As the first mover, Branch sparked the spatial audio boom, and hundreds of competitors and copycats appeared afterward. Kai stepped down in 2021; the company pivoted to a game studio and went on to raise a $12.5M seed.`,
    images: ['/branch/branch-1.jpg', '/branch/branch-2.jpg', '/branch/branch-3.jpg'],
    icon: 'branch.png',
    orbitBand: 2,
    bubbleRadius: 28,
    accent: '#9ad4b8',
    articles: [
      {
        label: 'TechCrunch — Virtual HQs race for remote work',
        href: 'https://techcrunch.com/2020/11/18/virtual-hqs-race-to-win-over-a-remote-work-fatigued-market/',
      },
      {
        label: 'Business Insider — Raising $1.5M via Discord and Clubhouse',
        href: 'https://www.businessinsider.com/first-time-founder-used-discord-clubhouse-to-raise-15-million-2020-12',
      },
    ],
  },
  {
    id: 'sitesire',
    title: 'Sitesire',
    subtitle: 'Web Development Studio',
    status: 'Inactive',
    date: '2018 - 2020',
    mapRole: 'inactive',
    description: `After Konjure, Kai built Sitesire into a top-rated Upwork web development studio, taking on custom design and engineering work for startups and larger companies alike.

The agency worked with 50+ clients, including Gartner and other Fortune 500 names, and generated hundreds of thousands of dollars in revenue before Kai shut it down to go all-in on Branch.`,
    images: ['/sitesire/sitesire-1.jpg', '/sitesire/sitesire-2.jpg', '/sitesire/sitesire-3.jpg'],
    icon: 'sitesire.png',
    orbitBand: 2,
    bubbleRadius: 26,
    accent: '#8cbfa8',
  },
  {
    id: 'konjure',
    title: 'Konjure',
    subtitle: 'Decentralized Website Builder',
    status: 'Inactive',
    date: '2017 - 2019',
    mapRole: 'inactive',
    description: `Kai co-founded Konjure with Ben Adamsky. They bootstrapped it and built on top of IPFS to create an early website builder that deployed websites onto a peer-to-peer network: decentralized websites.

Despite strong early experiments, they did not find durable product-market fit and shut the company down. Kai's only tattoo is the Konjure logo.`,
    images: [
      '/konjure/konjure-1.jpg',
      '/konjure/konjure-2.jpg',
      '/konjure/konjure-3.jpg',
      '/konjure/1_E_D3HiNjJQexb4YJwYpbBg.jpg',
      '/konjure/1_wPm4uCR1iFykOclgTBrSaw.jpg',
      '/konjure/DjUj6DqW4AECPhm.jpg',
      '/konjure/DkHAHRCV4AMbzHB.jpg',
      '/konjure/DloI0vHUYAAq7dq.jpg',
      '/konjure/Dp6KjQPUwAAnDwm.jpg',
    ],
    icon: 'konjure.png',
    orbitBand: 2,
    bubbleRadius: 26,
    accent: '#7ab898',
  },
  {
    id: 'godlycloud',
    title: 'GodlyCloud',
    subtitle: 'Cloud Computing',
    status: 'Acquired',
    date: '2017',
    mapRole: 'acquired',
    description: `After MyMCServers and DediFire, Kai co-founded GodlyCloud with Nir Zemer. The company focused on virtual private servers and cloud hosting internationally, with a strong presence in Europe and Asia, and served thousands of customers before merging with another host.`,
    images: ['/godlycloud/godlycloud-1.jpg', '/godlycloud/godlycloud-2.jpg', '/godlycloud/godlycloud-3.jpg'],
    icon: 'godlycloud.png',
    orbitBand: 3,
    bubbleRadius: 24,
    accent: '#6a9078',
  },
  {
    id: 'dedifire',
    title: 'DediFire',
    subtitle: 'Dedicated Server Hosting',
    status: 'Acquired',
    date: '2016 - 2017',
    mapRole: 'acquired',
    description: `DediFire sold dedicated, web, voice, and game server hosting across more than twenty titles.

Kai scaled it to hundreds of clients internationally, then sold the business to a UK gaming host. The buyer later became a well-known name in game hosting, especially around CS:GO.`,
    images: ['/dedifire/dedifire-1.jpg', '/dedifire/dedifire-2.jpg', '/dedifire/dedifire-3.jpg'],
    icon: 'dedifire.png',
    orbitBand: 3,
    bubbleRadius: 24,
    accent: '#6a9078',
  },
  {
    id: 'mymcservers',
    title: 'MyMCServers',
    subtitle: 'Game Server Hosting',
    status: 'Acquired',
    date: '2015',
    mapRole: 'acquired',
    description: `Kai co-founded MyMCServers with Nir Zemer, moving from operating Minecraft servers to hosting them at scale.

With a team of 12, the provider grew into one of the largest Minecraft hosts in the world, serving thousands of clients and hosting servers in locations around the world. Roughly nine months after launch, Kai sold it to a private buyer.`,
    images: ['/mymcservers/mymcservers-1.jpg', '/mymcservers/mymcservers-2.jpg', '/mymcservers/mymcservers-3.jpg'],
    icon: 'mymcservers.png',
    orbitBand: 3,
    bubbleRadius: 24,
    accent: '#6a9078',
  },
  {
    id: 'yeticrew',
    title: 'Yeti Crew',
    subtitle: 'Web Design Studio',
    status: 'Inactive',
    date: '2014 - 2015',
    mapRole: 'inactive',
    description: `Yeti Crew came about after Kai designed and developed websites for his own game servers, then scaled that into an agency for others.

He designed and built websites from scratch for game servers, gaming communities, hosting companies, and more. After dozens of successful large client projects, he moved on to focus on game server hosting.`,
    images: ['/yeticrew/yeticrew-1.jpg', '/yeticrew/yeticrew-2.jpg', '/yeticrew/yeticrew-3.jpg'],
    icon: 'yeticrew.png',
    orbitBand: 4,
    bubbleRadius: 22,
    accent: '#6b7c8f',
  },
  {
    id: 'craftfest',
    title: 'CraftFest',
    subtitle: 'Game Server',
    status: 'Inactive',
    date: '2012 - 2014',
    mapRole: 'inactive',
    description: `CraftFest started as a solo Minecraft world Kai played with his father and grew into something thousands of players enjoyed. It was his first startup and first online business, and he dropped out of high school while running it.

CraftFest is where Kai learned to code by developing Minecraft plugins and made his first money online. He later scaled it into a multi-server network with various minigames, monetized the ecosystem, and built it in parallel with a Minecraft gameplay YouTube channel.`,
    images: ['/craftfest/craftfest-1.jpg', '/craftfest/craftfest-2.jpg', '/craftfest/craftfest-3.jpg'],
    icon: 'craftfest.png',
    orbitBand: 4,
    bubbleRadius: 22,
    accent: '#6b7c8f',
  },
  {
    id: 'thiel_fellowship',
    title: 'Thiel Fellowship',
    subtitle: 'Thiel Foundation',
    status: 'Fellowship',
    date: '2023',
    mapRole: 'fellowship',
    description: `The Thiel Fellowship is a two-year grant from the Thiel Foundation for young people to leave school, get funded, and build a company or serious project full-time with mentorship and a peer cohort instead of a normal degree. Roughly twenty people per year make it from a huge applicant pool; by acceptance rate it is on the order of fifty times more selective than Harvard College.

Kai first applied at 17, got his first interview at 23, and after months of interviews and many calls was awarded the Thiel Fellowship for Cryopets, including a $100,000 grant.`,
    images: [],
    icon: 'thielfellowship.png',
    orbitBand: 3,
    bubbleRadius: 22,
    accent: '#c4a8ff',
    links: [{ label: 'thielfellowship.org', href: 'https://www.thielfellowship.org' }],
    articles: [
      {
        label: 'Business Wire — Thiel Foundation announces next fellow class',
        href: 'https://www.businesswire.com/news/home/20230208005685/en/Thiel-Foundation-Announces-Next-Thiel-Fellow-Class',
      },
      {
        label: 'Business Insider — Thiel Fellow cryopreserving pets',
        href: 'https://www.businessinsider.com/thiel-fellow-kai-micah-mills-cryopets-cryonics-longevity-animals-pets-2023-4',
      },
    ],
  },
  {
    id: 'foresight_fellowship',
    title: 'Foresight Fellowship',
    subtitle: 'Foresight Institute',
    status: 'Fellowship',
    date: '2022',
    mapRole: 'fellowship',
    description: `Foresight Institute is a long-running nonprofit focused on advancing frontier science and technology, especially work with long time horizons and large upside for civilization.

Kai received the Foresight Fellowship and met experts across aging and cryopreservation working on high-impact paths in life extension.`,
    images: [],
    icon: 'foresight.png',
    orbitBand: 3,
    bubbleRadius: 22,
    accent: '#b388ff',
    links: [{ label: 'foresight.org', href: 'https://foresight.org' }],
  },
  {
    id: 'longevity_biotech_fellowship',
    title: 'Longevity Biotech Fellowship',
    subtitle: 'On Deck',
    status: 'Fellowship',
    date: '2021',
    mapRole: 'fellowship',
    description: `Kai received the fellowship in the first-ever Longevity Biotech Fellowship cohort when it was still part of On Deck, before spinning out on its own.

LBF is a large community of people working across longevity biotech and the broader life extension space, led by Nathan Cheng.`,
    images: [],
    icon: 'lbf.png',
    orbitBand: 3,
    bubbleRadius: 22,
    accent: '#a078f0',
    links: [{ label: 'longbiofellowship.org', href: 'https://www.longbiofellowship.org' }],
  },
  {
    id: 'mta',
    title: 'Mormon Transhumanist Association',
    subtitle: 'Nonprofit community',
    status: 'Board',
    date: '2026 - Present',
    mapRole: 'board',
    description: `Founded in 2006, the Mormon Transhumanist Association is a nonprofit at the intersection of Latter-day Saint faith, theology, and transhumanism, advocating a compassionate use of science and technology toward human flourishing and futures worth hoping for. Kai joined the board for a three-year term on May 9th, 2026.`,
    images: ['/mta/mta.png'],
    icon: 'mta.png',
    orbitBand: 3,
    bubbleRadius: 22,
    accent: '#d9578a',
    links: [{ label: 'transfigurism.org', href: 'https://transfigurism.org' }],
  },
  {
    id: 'hm',
    title: 'Side Projects',
    subtitle: 'Honorable Mentions',
    status: 'Inactive',
    date: null,
    mapRole: 'inactive',
    description: `Scratch projects over the years: Vitalism, Antimortality, Longevity Tech Tree, WHICSIC, CovidCamp, Libs vs Cons, Lumberjack Larry, Bioshard, VaultCraft, Age of Alliance, AusPvP, Node24, Gravity Lift, CraftFestMC, TheCodeMC, FlexGaming, plus hundreds that never shipped. A moment of silence for those.`,
    images: [],
    icon: 'hm.png',
    orbitBand: 3,
    bubbleRadius: 20,
    accent: '#a89f91',
  },
  {
    id: 'investments',
    title: 'Investments',
    subtitle: 'Angel Portfolio',
    status: 'Investor',
    date: null,
    mapRole: 'investor',
    description: `Angel investments: Pallet (2020), Ender (2021), Transistor Bio (2021). No check sizes or syndicates on this page.`,
    images: [],
    icon: 'investments.png',
    orbitBand: 3,
    bubbleRadius: 22,
    accent: '#c9b870',
  },
  {
    id: 'personal',
    title: 'Pictures',
    subtitle: 'Email for Press',
    status: null,
    date: null,
    description: `For press photos or interviews, email kaimicahmills@gmail.com with your outlet, deadline, and what you need.`,
    images: ['/personal/kai-1.jpg', '/personal/kai-2.jpg', '/personal/kai-3.jpg'],
    icon: 'personal.png',
    orbitBand: 3,
    bubbleRadius: 22,
    accent: '#9dd4b0',
  },
];

/** Main orbit nodes on the power map (hub links to each of these). */
const POWER_MAP_ORDER = [
  'cryopets',
  'hydradao',
  'cryodao',
  'branch',
  'sitesire',
  'konjure',
  'godlycloud',
  'dedifire',
  'mymcservers',
  'yeticrew',
  'craftfest',
  'thiel_fellowship',
  'foresight_fellowship',
  'longevity_biotech_fellowship',
  'mta',
];
export const POWER_MAP_NODES = POWER_MAP_ORDER.map((id) => ORBIT_NODES.find((n) => n.id === id)).filter(Boolean);

export function getNodeById(id) {
  if (id === CENTER_NODE.id) return CENTER_NODE;
  if (id === HIDDEN_NODE.id) return HIDDEN_NODE;
  return ORBIT_NODES.find((n) => n.id === id) ?? null;
}

/** Resolve hub, orbit, or moon id for the detail panel. */
export function getDetailForMapClick(id) {
  const main = getNodeById(id);
  if (main) return main;
  const moon = MOON_NODES.find((m) => m.id === id);
  if (!moon) return null;
  return {
    id: moon.id,
    title: moon.label,
    subtitle: moon.detailSubtitle ?? 'On the map',
    status: moon.detailStatus ?? null,
    mapRole: moon.mapRole ?? null,
    date: moon.detailDate ?? null,
    description: moon.detailDescription ?? '',
    images: moon.detailImages ?? [],
    links: moon.detailLinks ?? [],
    articles: moon.detailArticles ?? [],
  };
}
